const currentYear = new Date().getFullYear();

/**
 * `searchDefaults` - Sort options for search results
 * @constant
 * @default
 */
export const searchDefaults = {
  ranges: {
    hours: {
      max: 10000,
      min: 0,
      step: 500,
    },
    miles: {
      max: 600000,
      min: 0,
      step: 5000,
    },
    meter: {
      max: 100,
      min: 0,
      step: 1,
    },
    price: {
      max: 100000,
      min: 0,
      step: 5000,
    },
    year: {
      max: currentYear + 1,
      min: currentYear - 60,
      step: 1,
    },
    wheelbase: {
      max: 266,
      min: 90,
      step: 1,
    },
    grossVehicleWeight: {
      max: 86000,
      min: 7000,
      step: 500,
    },
    sleeperSize: {
      max: 80,
      min: 48,
      step: 1,
    },
    frontAxleRating: {
      max: 20000,
      min: 6830,
      step: 500,
    },
    rearAxleRating: {
      max: 46000,
      min: 13660,
      step: 500,
    },
    horsepower: {
      max: 485,
      min: 270,
      step: 5,
    },
    transmissionSpeed: {
      max: 16,
      min: 5,
      step: 1,
    },
  },
  tip: "Any",
};

/**
 * `SITE_BUSINESS_UNIT_OPTIONS` - Source options for search filters
 * @constant sitebusinessunitoptions
 * @default
 */
export const SITE_BUSINESS_UNIT_OPTIONS = [
  {
    id: "holt-cat",
    label: "HOLT Cat",
    sources: [
      "attachments",
      "catFinancial",
      "consignment",
      "heavy",
      "heavyUsed",
      "jv",
    ],
  },
  {
    id: "holt-crane-equipment",
    label: "HOLT Crane & Equipment",
    sources: ["crane"],
  },
  {
    id: "holt-truck-centers",
    label: "HOLT Truck Centers",
    sources: ["kyrish", "trailer", "truck"],
  },
  {
    id: "texas-first-rentals",
    label: "Texas First Rentals",
    sources: ["texasFirst"],
  },
];

/**
 * `SITE_KEY_ACCOUNT_OPTIONS` - Source options for search filters
 * @constant sitekeyaccountoptions
 * @default
 */
export const SITE_KEY_ACCOUNT_OPTIONS = [
  {
    label: "HOLT Heavy Rental Pull List",
    value: "9000497",
    id: "holt-heavy-rental-pull-list",
  },
];

/**
 * `SITE_MAKE_OPTIONS` - Make options for search filters
 * @constant sitemakeoptions
 * @default
 */
export const SITE_MAKE_OPTIONS = [
  { label: "Caterpillar", value: "CAT" },
  { label: "Non-CAT", value: "non-cat" },
];

/**
 * `SITE_VISIBILITY_OPTIONS` - VIsibility options for search filters
 * @constant sitevisibilityoptions
 * @default
 */
export const SITE_VISIBILITY_OPTIONS = [
  { label: "Internal only", value: "internal" },
  { label: "Public only", value: "public" },
];

/**
 * `SITE_SORT_OPTIONS` - Sort options for search results
 * @constant sitesortoptions
 * @default
 */
export const SITE_SORT_OPTIONS = [
  { label: "Most Recently Added", value: "created_at;dsc" },
  { label: "Most Recently Updated", value: "updated_at;dsc" },
  { label: "Meter (High)", value: "meterValue;dsc" },
  { label: "Meter (Low)", value: "meterValue;asc" },
  { label: "Price (High)", value: "advertisedPrice;dsc" },
  { label: "Price (Low)", value: "advertisedPrice;asc" },
  { label: "Year (Newer)", value: "year;dsc" },
  { label: "Year (Older)", value: "year;asc" },
];

/**
 * `SITE_SOURCE_OPTIONS` - Source options for search filters
 * @constant sitesourceoptions
 * @default
 */
export const SITE_SOURCE_OPTIONS = [
  { label: "Attachments", value: "attachments", id: "attachments" },
  { label: "CAT Financial", value: "catFinancial", id: "cat-financial" },
  { label: "Consignment", value: "consignment", id: "consignment" },
  { label: "Cranes", value: "crane", id: "crane" },
  { label: "Joint Venture", value: "jv", id: "jv" },
  { label: "HOLT Heavy Rentals", value: "heavy", id: "heavy" },
  { label: "HOLT Heavy Used Equipment", value: "heavyUsed", id: "heavyUsed" },
  { label: "Kyrish", value: "kyrish", id: "kyrish" },
  { label: "Texas First Rentals", value: "texasFirst", id: "texas-first" },
  { label: "Trailers", value: "trailer", id: "trailer" },
  { label: "Trucks", value: "truck", id: "truck" },
];

/**
 * `SITE_DISTANCE_OPTIONS` - Show distance for search results
 * @constant sitedistanceoptions
 * @default
 */
export const SITE_DISTANCE_OPTIONS = [
  { label: "<50 miles", value: "50" },
  { label: "<100 miles", value: "100" },
  { label: "<150 miles", value: "150" },
  { label: "≤200 miles", value: "200" },
];

/**
 * `SITE_SHOW_OPTIONS` - Show options for search results
 * @constant siteshowoptions
 * @default
 */
export const SITE_SHOW_OPTIONS = [
  { label: "24 per page", value: "24" },
  { label: "48 per page", value: "48" },
  { label: "100 per page", value: "100" },
];

/**
 * `SITE_SHOW_OPTIONS_DEFAULT` - Default show options for search results
 * @constant siteshowoptionsdefault
 * @default
 */
export const SITE_SHOW_OPTIONS_DEFAULT = parseInt(
  SITE_SHOW_OPTIONS[0].value,
  10
);

/**
 * `SITE_KEY_ACCOUNT_OPTIONS` - Source options for search filters
 * @constant sitekeyaccountoptions
 * @default
 */
export const SITE_STATUS_OPTIONS = [
  {
    label: "Available",
    value: "available",
    id: "available",
  },
  {
    label: "Sale Pending",
    value: "pending",
    id: "pending",
  },
];

/**
 * `SITE_CONSTRUCTION_MAKE_OPTIONS` - Construction make options for search filters
 * @constant siteconstructionmakeoptions
 * @default
 */
export const SITE_CONSTRUCTION_MAKE_OPTIONS = [
  { label: "Barber Greene", value: "Barber Greene", id: "barber-greene" },
  { label: "Bell", value: "Bell", id: "bell" },
  { label: "Bobcat", value: "Bobcat", id: "bobcat" },
  { label: "Bomag", value: "Bomag", id: "bomag" },
  { label: "Case", value: "Case", id: "case" },
  { label: "Caterpillar", value: "CAT", id: "cat" },
  { label: "Challenger", value: "Challenger", id: "challenger" },
  { label: "Dynapac", value: "Dynapac", id: "dynapac" },
  { label: "Genie", value: "Genie", id: "genie" },
  { label: "Gradall", value: "Gradall", id: "gradall" },
  { label: "Hitachi", value: "Hitachi", id: "hitachi" },
  { label: "Horsch", value: "Horsch", id: "horsch" },
  { label: "JCB", value: "JCB", id: "jcb" },
  { label: "John Deere", value: "John Deere", id: "john-deere" },
  { label: "Kawasaki", value: "Kawasaki", id: "kawasaki" },
  { label: "Kobelco", value: "Kobelco", id: "kobelco" },
  { label: "Komatsu", value: "Komatsu", id: "komatsu" },
  { label: "Kubota", value: "Kubota", id: "kubota" },
  { label: "Laymor", value: "Laymor", id: "laymor" },
  { label: "Ledwell", value: "Ledwell", id: "ledwell" },
  { label: "Lee Boy", value: "Lee Boy", id: "lee-boy" },
  { label: "Mahindra", value: "Mahindra", id: "mahindra" },
  { label: "Massey Ferguson", value: "Massey Ferguson", id: "massey-ferguson" },
  { label: "New Holland", value: "New Holland", id: "new-holland" },
  { label: "Roadtech", value: "Roadtech", id: "roadtech" },
  { label: "Vermeer", value: "Vermeer", id: "vermeer" },
  { label: "Volvo", value: "Volvo", id: "volvo" },
  { label: "Weiler", value: "Weiler", id: "weiler" },
  { label: "Yanmar", value: "Yanmar", id: "yanmar" },
];

export const SITE_TRUCK_MAKE_OPTIONS = [
  { label: "Caterpillar", value: "CAT", id: "cat" },
  { label: "Chevrolet", value: "Chevrolet", id: "chevrolet" },
  { label: "Dennis Eagle", value: "Dennis Eagle", id: "dennis-eagle" },
  { label: "Ford", value: "Ford", id: "ford" },
  { label: "Freightliner", value: "Freightliner", id: "freightliner" },
  { label: "Hino", value: "Hino", id: "hino" },
  { label: "International", value: "International", id: "international" },
  { label: "Isuzu", value: "Isuzu", id: "isuzu" },
  { label: "Kenworth", value: "Kenworth", id: "kenworth" },
  { label: "Mack", value: "Mack", id: "mack" },
  { label: "Peterbilt", value: "Peterbilt", id: "peterbilt" },
  { label: "Ram", value: "Ram", id: "ram" },
  { label: "Volvo", value: "Volvo", id: "volvo" },
  { label: "Western Star", value: "Western Star", id: "western-star" },
];

export const SITE_TRUCK_BODY_TYPE_OPTIONS = [
  { label: "Dump Body", value: "Dump Body", id: "dump-body" },
  { label: "Flatbed", value: "Flatbed", id: "flatbed" },
  { label: "Landscape", value: "Landscape", id: "landscape" },
  { label: "Pickup", value: "Pickup", id: "pickup" },
  { label: "Reefer", value: "Reefer", id: "reefer" },
  { label: "Refuse", value: "Refuse", id: "refuse" },
];

export const SITE_TRUCK_UNIT_MODEL_CLASS_OPTIONS = [
  { label: "Light", value: "Light", id: "light" },
  { label: "Medium", value: "Medium", id: "medium" },
  { label: "Heavy", value: "Heavy", id: "heavy" },
  { label: "Severe", value: "Severe", id: "severe" },
];

export const SITE_TRUCK_CAB_TYPE_OPTIONS = [
  { value: "Sleeper Cab", label: "Sleeper", id: "sleeper" },
  { value: "Day Cab", label: "Day", id: "day" },
  { value: "Regular Cab", label: "Regular", id: "regular" },
  { value: "Crew Cab", label: "Crew", id: "crew" },
];

export const SITE_TRUCK_ENGINE_MAKE_OPTIONS = [
  { label: "Caterpillar", value: "Caterpillar", id: "caterpillar" },
  { label: "Cummins", value: "Cummins", id: "cummins" },
  { label: "Detroit", value: "Detroit", id: "detroit" },
  { label: "Ford", value: "Ford", id: "ford" },
  { label: "GMC", value: "GMC", id: "gmc" },
  { label: "International", value: "International", id: "international" },
  { label: "Isuzu", value: "Isuzu", id: "isuzu" },
  { label: "Mack", value: "Mack", id: "mack" },
  { label: "Paccar", value: "Paccar", id: "paccar" },
];

export const SITE_TRUCK_TRANSMISSION_TYPE_OPTIONS = [
  {
    label: "Automated Manual",
    value: "Automated Manual",
    id: "automated-manual",
  },
  { label: "Automatic", value: "Automatic", id: "automatic" },
  { label: "Manual", value: "Manual", id: "manual" },
];
